import type { CollectionSiteSchema } from "../types/collection-site.type";
import type {
  EsgBranchMonthlyRow,
  EsgDashboardByBranchResponse,
} from "../types/esg-dashboard.type";
import type {
  BranchOperationalRow,
  DashboardByBranchResponse,
} from "../types/dashboard.type";

const UNASSIGNED_SITE_ID = "__unassigned__";

function safeNum(value: number | null | undefined): number {
  if (value == null || Number.isNaN(Number(value))) return 0;
  return Number(value);
}

export function siteDisplayName(site: CollectionSiteSchema): string | null {
  const data = site.data ?? {};
  const candidates = [
    data.name,
    data.branch_name,
    data.title,
    data.site_name,
  ];
  for (const raw of candidates) {
    if (typeof raw === "string" && raw.trim()) {
      return raw.trim();
    }
  }
  return null;
}

function siteContact(site: CollectionSiteSchema): string | null {
  const contact = site.data?.contact_info;
  if (!contact || typeof contact !== "object") return null;
  const c = contact as Record<string, unknown>;
  const phone = typeof c.phone === "string" ? c.phone.trim() : "";
  return phone || null;
}

function buildSiteMetaMap(
  sites: CollectionSiteSchema[],
): Map<string, CollectionSiteSchema> {
  return new Map(sites.map((site) => [site.id, site]));
}

export function mapEsgBranchToOperationalRow(
  row: EsgBranchMonthlyRow,
  siteMeta?: CollectionSiteSchema | null,
  isUnassigned = false,
): BranchOperationalRow {
  const isUnassignedRow =
    isUnassigned || row.collection_site_id === UNASSIGNED_SITE_ID;

  return {
    collection_site_id: row.collection_site_id,
    name: isUnassignedRow
      ? null
      : row.collection_site_name ?? (siteMeta ? siteDisplayName(siteMeta) : null),
    latitude: siteMeta?.latitude ?? null,
    longitude: siteMeta?.longitude ?? null,
    branch_contact: siteMeta ? siteContact(siteMeta) : null,
    total_pickup_orders: null,
    total_waste_kg: safeNum(row.total_waste_diverted_kg),
    completed_waste_kg: safeNum(row.total_waste_recycled_kg),
    total_revenue: null,
    waste_diversion_rate_percent: safeNum(row.waste_diversion_rate_percent),
    employee_count: null,
    container_count: null,
    fill_level_report_count: safeNum(row.fill_level_report_count),
    materials: row.recycled_waste_breakdown_by_type ?? [],
  };
}

export function mapEsgByBranchResponse(
  response: EsgDashboardByBranchResponse,
  monthDate: string,
  collectionSites: CollectionSiteSchema[] = [],
): DashboardByBranchResponse {
  const siteMeta = buildSiteMetaMap(collectionSites);

  const branches = (response.branches ?? []).map((row) =>
    mapEsgBranchToOperationalRow(row, siteMeta.get(row.collection_site_id)),
  );

  const unassigned_waste = response.unassigned_waste
    ? mapEsgBranchToOperationalRow(response.unassigned_waste, null, true)
    : null;

  return {
    organization_id: response.organization_id,
    month_date: response.month_date ?? monthDate,
    total_pickup_orders: safeNum(response.total_orders),
    total_waste_kg: safeNum(response.total_waste_amount),
    completed_waste_kg: safeNum(response.completed_waste_amount),
    total_revenue: safeNum(response.total_revenue),
    waste_diversion_rate_percent: safeNum(response.waste_diversion_rate),
    previous_month_total_pickup_orders: safeNum(response.previous_month_total_orders),
    previous_month_total_waste_kg: safeNum(response.previous_month_total_waste_amount),
    previous_month_completed_waste_kg: safeNum(response.previous_month_completed_waste_amount),
    previous_month_total_revenue: safeNum(response.previous_month_total_revenue),
    previous_month_waste_diversion_rate_percent: safeNum(
      response.previous_month_waste_diversion_rate,
    ),
    branches,
    unassigned_waste,
  };
}

/** When JWT has no site list, treat as org-wide access (all branches). */
export function resolveBranchSiteAccess(
  sitesAccess: string[] | "all" | "none",
): string[] | "all" | "none" {
  if (sitesAccess === "none") {
    return "all";
  }
  return sitesAccess;
}
